import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/Button';
import { Menu } from 'primereact/menu';
import { Skeleton } from 'primereact/skeleton';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';

import Layout from '../../Layout/Layout';
import { useGetVideoWalls, useDeleteVideoWall } from '../../../queries/videoWall';
import useWallSyncTelemetry from './useWallSyncTelemetry';
import GridPreview from './components/GridPreview';
import WallLivePreview from './preview/WallLivePreview';
import WallTabs from './components/WallTabs';
import { useIsRestaurant } from '../../../hooks/useAccountType';
import FeatureLocked from '../../SharedComponents/FeatureLocked';

/**
 * Video Wall — list page. Shows every wall the company has built, each as a
 * card with a live mini preview (while playing) or a to-scale grid, screen
 * count and live state. The header keeps three primary actions + a "More"
 * overflow; the empty state launches the setup guide.
 */

/* One wall card. Uses RTDB telemetry for the live state + mini preview, falling
   back to the stored DB state for stopped walls. */
function WallCard({ wall, onOpen, onDelete }) {
  const { t } = useTranslation();
  const tel = useWallSyncTelemetry(wall.id);

  const live = tel.state || wall.state;
  const playing = live === 'playing';
  const preparing = live === 'preparing';
  const active = playing || preparing;
  const liveScreens = Object.values(tel.screens || {}).filter(Boolean).length;

  const stateLabel = preparing ? t('video_wall.state_preparing')
    : playing ? t('video_wall.state_playing')
    : t('video_wall.state_stopped');
  const pillBg = playing ? 'var(--pp-success)' : preparing ? 'var(--pp-warning)' : 'var(--pp-bg-tertiary)';
  const pillFg = active ? '#fff' : 'var(--pp-text-secondary)';

  return (
    <div
      className="pp-card-clickable h-100 p-3 d-flex flex-column"
      style={{
        background: 'var(--pp-bg-elevated)',
        border: '1px solid var(--pp-border)',
        borderRadius: 'var(--pp-radius-lg)',
        boxShadow: 'var(--pp-shadow-sm)',
      }}
      role="button" tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
    >
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h5 className="mb-0 text-truncate" style={{ color: 'var(--pp-text-primary)' }}>{wall.name}</h5>
        <span
          className="pp-status d-inline-flex align-items-center gap-1"
          style={{
            background: pillBg, color: pillFg,
            border: 'none', fontWeight: 600, padding: '2px 10px', borderRadius: 999, fontSize: 12, flexShrink: 0,
          }}
        >
          {active && <span style={{ width: 7, height: 7, borderRadius: 999, background: '#fff', display: 'inline-block' }} />}
          {stateLabel}
        </span>
      </div>

      {/* Live mini preview while playing; otherwise the scale grid diagram. */}
      <div className="d-flex justify-content-center my-2">
        {playing
          ? <WallLivePreview wallId={wall.id} maxWidth={220} />
          : <GridPreview rows={wall.rows} columns={wall.columns} orientation={wall.orientation} maxWidth={220} />}
      </div>

      <div className="mt-auto pt-2">
        <small className="d-block mb-2" style={{ color: 'var(--pp-text-secondary)' }}>
          {wall.rows}×{wall.columns} · {active && liveScreens > 0
            ? t('video_wall.live_screens', { n: liveScreens, total: wall.screens_count ?? liveScreens })
            : t('video_wall.screens_count', { n: wall.screens_count ?? 0 })}
        </small>
        <div className="pp-card-actions">
          <Button
            size="small" icon="pi pi-arrow-right" iconPos="right" label={t('video_wall.open')}
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
          />
          <Button
            className="pp-card-del" icon="pi pi-trash" rounded text severity="danger" size="small"
            aria-label={t('video_wall.delete')}
            onClick={(e) => { e.stopPropagation(); onDelete(wall); }}
          />
        </div>
      </div>
    </div>
  );
}

export default function VideoWallIndex() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: walls = [], isLoading, error } = useGetVideoWalls();
  const del = useDeleteVideoWall();
  const moreRef = React.useRef(null);
  const isRestaurant = useIsRestaurant();

  const confirmDelete = (wall) =>
    confirmDialog({
      message: t('video_wall.delete_confirm', { name: wall.name }),
      header: t('video_wall.delete_title'),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('video_wall.delete'),
      rejectLabel: t('video_wall.cancel'),
      accept: () => del.mutate(wall.id),
    });

  // Secondary destinations live behind a single "More" menu so the header keeps
  // a clear primary hierarchy (Create · Library · Schedule).
  const moreItems = [
    { label: t('video_wall.setup_guide'), icon: 'pi pi-compass', command: () => navigate('/apps/video-wall/create') },
    { separator: true },
    { label: t('video_wall.locations.title'), icon: 'pi pi-map-marker', command: () => navigate('/apps/video-wall/locations') },
    { label: t('video_wall.groups.title'), icon: 'pi pi-object-group', command: () => navigate('/apps/video-wall/groups') },
    { label: t('video_wall.menus.title'), icon: 'pi pi-list', command: () => navigate('/apps/video-wall/menus') },
    { label: t('video_wall.templates.title'), icon: 'pi pi-palette', command: () => navigate('/apps/video-wall/templates') },
    { label: t('video_wall.simulator.title'), icon: 'pi pi-desktop', command: () => navigate('/apps/video-wall/simulator') },
  ];

  if (error?.response?.status === 402) {
    return (
      <Layout>
        <FeatureLocked feature="video_wall" message={error.response.data?.message} />
      </Layout>
    );
  }

  return (
    <Layout>
      <ConfirmDialog />

      {/* Restaurant-only sub-nav (replaces the "More" dropdown for them) */}
      <WallTabs />

      {/* Hero */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <h2 className="mb-1" style={{ color: 'var(--pp-text-primary)', fontWeight: 700 }}>
            <i className="fas fa-th-large me-2" style={{ color: 'var(--pp-primary)' }} />
            {t('video_wall.title')}
          </h2>
          <p className="mb-0" style={{ color: 'var(--pp-text-secondary)' }}>
            {t('video_wall.hero_subtitle')}
          </p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Button
            label={t('video_wall.library.title')}
            icon="pi pi-photo-video"
            outlined
            onClick={() => navigate('/apps/video-wall/library')}
          />
          <Button
            label={t('video_wall.schedule.title')}
            icon="pi pi-calendar"
            outlined
            onClick={() => navigate('/apps/video-wall/schedule')}
          />
          {!isRestaurant && (
            <>
              <Button
                label={t('video_wall.more')}
                icon="pi pi-ellipsis-h"
                outlined
                onClick={(e) => moreRef.current?.toggle(e)}
                aria-haspopup
              />
              <Menu model={moreItems} popup ref={moreRef} />
            </>
          )}
          <Button
            label={t('video_wall.create_new')}
            icon="pi pi-plus"
            onClick={() => navigate('/apps/video-wall/create')}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="row g-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="col-12 col-md-6 col-lg-4"><Skeleton height="13rem" /></div>
          ))}
        </div>
      ) : walls.length === 0 ? (
        /* Empty state → setup-guide launchpad */
        <div
          className="text-center mx-auto p-5"
          style={{
            maxWidth: 560,
            background: 'var(--pp-bg-secondary)',
            border: '1px dashed var(--pp-border)',
            borderRadius: 'var(--pp-radius-lg)',
          }}
        >
          <div style={{ fontSize: '3rem' }}>🧱</div>
          <h4 className="mt-3" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.empty_title')}</h4>
          <p style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.empty_lead')}</p>

          <div className="text-start mx-auto my-4" style={{ maxWidth: 360 }}>
            {[t('video_wall.empty_step_1'), t('video_wall.empty_step_2'), t('video_wall.empty_step_3')].map((s, i) => (
              <div key={i} className="d-flex align-items-center gap-3 mb-2">
                <span style={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                  background: 'var(--pp-primary)', color: '#fff',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13,
                }}>{i + 1}</span>
                <span style={{ color: 'var(--pp-text-primary)' }}>{s}</span>
              </div>
            ))}
          </div>

          <Button
            label={t('video_wall.empty_cta')}
            icon="pi pi-compass"
            size="large"
            onClick={() => navigate('/apps/video-wall/create')}
          />
        </div>
      ) : (
        <div className="row g-3">
          {walls.map((wall) => (
            <div key={wall.id} className="col-12 col-md-6 col-lg-4">
              <WallCard
                wall={wall}
                onOpen={() => navigate(`/apps/video-wall/${wall.id}`)}
                onDelete={confirmDelete}
              />
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
